import { useEffect, useState } from "react";
import { RoomStateEvent, type MatrixClient, type Room, type RoomMember } from "matrix-js-sdk";
import { getProfiles } from "../../api/client";
import type { UserProfile } from "../../api/types";
import { listRoomMembers } from "../../matrix/rooms";

/**
 * Fetch provisioning profiles (incl. company) for every member of a room,
 * keyed by matrix user id. Refetches when membership changes. Used for group
 * member lists and per-message sender labels.
 */
export function useRoomMemberProfiles(
  client: MatrixClient,
  room: Room,
): Map<string, UserProfile> {
  const [profiles, setProfiles] = useState<Map<string, UserProfile>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = () => {
      const ids = listRoomMembers(room).map((m) => m.userId);
      const token = client.getAccessToken();
      if (!token || ids.length === 0) {
        setProfiles(new Map());
        return;
      }
      getProfiles(ids, token, controller.signal)
        .then((rows) => {
          if (cancelled) return;
          setProfiles(new Map(rows.map((p) => [p.matrixUserId, p])));
        })
        .catch(() => undefined); // best-effort; labels fall back to display names
    };

    load();

    const onMembers = (_event: unknown, _state: unknown, member: RoomMember) => {
      if (member.roomId === room.roomId) load();
    };
    client.on(RoomStateEvent.Members, onMembers);

    return () => {
      cancelled = true;
      controller.abort();
      client.removeListener(RoomStateEvent.Members, onMembers);
    };
  }, [client, room]);

  return profiles;
}
